import React from 'react';
import { pdf } from '@react-pdf/renderer';
import ComprehensiveKYPDocument from '../components/pdf/ComprehensiveKYPDocument';
import ProfessionalKYPDocument from '../components/pdf/ProfessionalKYPDocument';
import ProfessionalFALDocument from '../components/pdf/ProfessionalFALDocument';
import ComprehensiveFALDocument from '../components/pdf/ComprehensiveFALDocument';
import { getApplication } from '../services/applicationService';
import { getLeadGeneratorById } from '../services/leadGeneratorService';

export const generateKYPPDF = async (application, kypData, { template = 'professional', leadGeneratorData } = {}) => {
  const buildDoc = (tpl) => (
    tpl === 'professional'
      ? <ProfessionalKYPDocument application={application} kypData={kypData} leadGeneratorData={leadGeneratorData} />
      : <ComprehensiveKYPDocument application={application} kypData={kypData} leadGeneratorData={leadGeneratorData} />
  );

  const minimalDoc = (
    <ComprehensiveKYPDocument
      application={{ applicationId: application?.applicationId, patient: application?.patient || {} }}
      kypData={{ applicationDetails: { applicationId: application?.applicationId, patientName: application?.patient?.name }, clinicalNotes: 'Preview fallback document.' }}
      leadGeneratorData={leadGeneratorData}
    />
  );

  const tryRender = async (doc, tplLabel) => {
    // Validate the element to avoid library internals accessing null.props
    if (!doc || typeof doc !== 'object' || doc === null) {
      throw new Error('Invalid document element');
    }
    const instance = pdf();
    instance.updateContainer(doc);
    const blob = await instance.toBlob();
    return { success: true, blob, fileName: `KYP_${tplLabel}_${application?.applicationId || Date.now()}_${Date.now()}.pdf` };
  };

  try {
    // First attempt: requested template
    return await tryRender(buildDoc(template), template === 'professional' ? 'Official' : 'Comprehensive');
  } catch (err1) {
    console.error('KYP PDF first attempt failed:', err1);
    try {
      // Second attempt: alternate template
      const alt = template === 'professional' ? 'comprehensive' : 'professional';
      return await tryRender(buildDoc(alt), alt === 'professional' ? 'Official' : 'Comprehensive');
    } catch (err2) {
      console.error('KYP PDF alternate template failed:', err2);
      try {
        // Final attempt: minimal safe document
        return await tryRender(minimalDoc, 'Fallback');
      } catch (err3) {
        console.error('KYP PDF minimal fallback failed:', err3);
        return { success: false, error: err3?.message || 'Failed to generate KYP PDF' };
      }
    }
  }
};

export const downloadKYPPDF = async (application, kypData, fileName, options = {}) => {
  try {
  const result = await generateKYPPDF(application, kypData, options);
    
    if (!result.success) {
      throw new Error(result.error);
    }

    // Create download link
    const url = URL.createObjectURL(result.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || result.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Error downloading KYP PDF:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

export const previewKYPPDF = async (application, kypData, options = {}) => {
  try {
  const result = await generateKYPPDF(application, kypData, options);
    
    if (!result.success) {
      throw new Error(result.error);
    }

    // Create preview URL
    const url = URL.createObjectURL(result.blob);
    window.open(url, '_blank');

    return { success: true };
  } catch (error) {
    console.error('Error previewing KYP PDF:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Real-time Firestore-backed generator: fetch latest application and compose KYP data
export const composeRealtimeKYPData = (app) => {
  const wf = app?.workflow || {};
  const mv = wf.medicalVerification || {};
  const pv = wf.policyVerification || {};

  const det = {
    applicationId: app?.applicationId,
    patientName: app?.patient?.name,
    diagnosis: app?.medical?.diagnosis || app?.medical?.treatments?.[0]?.diagnosisName,
    treatmentType: app?.medical?.treatmentType,
    procedureName: app?.medical?.procedureName || app?.medical?.treatments?.[0]?.procedureName || 'As per diagnosis',
    admissionDate: app?.medical?.admissionDate,
    dischargeDate: app?.medical?.dischargeDate || '',
    hospitalName: app?.hospitalName || app?.hospital?.name,
    insuranceCompany: app?.policy?.insuranceCompany,
    policyNumber: app?.policy?.number,
    policyPeriod: app?.policy?.startDate && app?.policy?.endDate ? `${app.policy.startDate} to ${app.policy.endDate}` : undefined,
    primaryInsuredName: app?.policyHolder?.isSelf ? app?.patient?.name : app?.policyHolder?.name,
    sumInsured: pv.policyDetails?.baseSumInsured || 0,
    cumulativeBonus: Number(pv.coverageBreakdown?.cumulativeBonus || pv.policyDetails?.cumulativeBonus || 0),
    topupSI: Number(pv.policyDetails?.topUpSumInsured || 0),
    effectiveCoverage: Number(pv.coverageBreakdown?.effectiveTotalSumInsured || 0),
    balanceSumInsured: Number(pv.coverageBreakdown?.balanceSumInsured || app?.policy?.balanceSumInsured || 0),
    intimationId: app?.kypData?.applicationDetails?.intimationId || pv.intimationDetails?.intimationId || '',
    roomEligibility: pv.roomEligibility?.roomType || app?.kypData?.roomEligibility || 'As per policy',
    roomAmountPerDay: pv.roomEligibility?.roomAmountPerDay || 0,
    icuAmountPerDay: pv.roomEligibility?.icuAmountPerDay || 0,
    city: app?.leadGenerator?.city || 'Not specified',
    estimatedCost: app?.medical?.estimatedCost || 0,
  };

  const coverageBreakdown = pv.coverageBreakdown || {
    baseCoverage: Number(pv.policyDetails?.baseSumInsured || 0),
    cumulativeBonus: Number(pv.policyDetails?.cumulativeBonus || 0),
    topUpCoverage: Number(pv.policyDetails?.topUpSumInsured || 0),
    effectiveTotalSumInsured: Number(pv.policyDetails?.effectiveTotalSumInsured || 0),
    utilized: Number(pv.coverageBreakdown?.utilized || 0),
    balanceSumInsured: Number(pv.coverageBreakdown?.balanceSumInsured || 0),
  };

  const policyDetails = pv.policyDetails || {};
  const coPayStructure = pv.coPayStructure || policyDetails.coPayStructure;
  const deductiblesAndSublimits = pv.deductiblesAndSublimits || policyDetails.deductiblesAndSublimits;
  const intimationDetails = pv.intimationDetails || policyDetails.intimationDetails || {};
  const claimHistory = pv.claimHistory || policyDetails.claimHistory || {};
  const policyAnalysis = pv.policyAnalysis || {};
  const roomEligibilityObj = pv.roomEligibility || {};

  const mergedKyp = {
    ...(mv.kypData || wf?.kypData || {}),
    applicationDetails: { ...(mv.kypData?.applicationDetails || {}), ...det },
    coverageBreakdown,
    policyDetails: {
      ...policyDetails,
      coPayStructure: coPayStructure || {},
      deductiblesAndSublimits: deductiblesAndSublimits || {},
    },
  // Bring important policy verification sections to top-level for PDF consumption
  intimationDetails,
  claimHistory,
  policyAnalysis,
  roomEligibility: roomEligibilityObj,
  // Also surface these at top-level for templates that access them directly
  coPayStructure: coPayStructure || {},
  deductiblesAndSublimits: deductiblesAndSublimits || {},
    // If explicit flags exist, use them; else infer "true" when medical verification is approved or KYP is generated
    documentsAuthentic: (() => {
      if (mv.documentsAuthentic !== undefined) return Boolean(mv.documentsAuthentic);
      const mvw = app?.workflow?.medicalVerification;
      if (mvw?.documentsAuthentic !== undefined) return Boolean(mvw.documentsAuthentic);
      return Boolean(mvw?.status === 'approved' || mvw?.kypGenerated === true);
    })(),
    medicalHistoryVerified: (() => {
      if (mv.medicalHistoryVerified !== undefined) return Boolean(mv.medicalHistoryVerified);
      const mvw = app?.workflow?.medicalVerification;
      if (mvw?.medicalHistoryVerified !== undefined) return Boolean(mvw.medicalHistoryVerified);
      return Boolean(mvw?.status === 'approved' || mvw?.kypGenerated === true);
    })(),
    treatmentAppropriate: (() => {
      if (mv.treatmentAppropriate !== undefined) return Boolean(mv.treatmentAppropriate);
      const mvw = app?.workflow?.medicalVerification;
      if (mvw?.treatmentAppropriate !== undefined) return Boolean(mvw.treatmentAppropriate);
      return Boolean(mvw?.status === 'approved' || mvw?.kypGenerated === true);
    })(),
    coPay: (mv.kypData?.coPay || undefined),
    subLimit: (mv.kypData?.subLimit || undefined),
    waitingPeriod: mv.kypData?.waitingPeriod || '30 Days',
    specificWaiting: mv.kypData?.specificWaiting || '2 Years',
    pedWaiting: mv.kypData?.pedWaiting || '4 Years',
    approvedDisbursementAmount: Number(mv.approvedDisbursementAmount || mv.kypData?.approvedDisbursementAmount || 0),
    disbursementPercentage: Number(mv.kypData?.disbursementPercentage || 0),
    disbursementCalculation: mv.kypData?.disbursementCalculation || {
      totalEstimatedCost: Number(app?.medical?.estimatedCost || 0),
      approvedPercentage: Number(mv.kypData?.disbursementPercentage || 0),
      approvedAmount: Number(mv.approvedDisbursementAmount || mv.kypData?.approvedDisbursementAmount || 0),
      disbursementNotes: mv.kypData?.disbursementNotes || '',
    },
    clinicalNotes: mv.kypData?.clinicalNotes || app?.kypData?.clinicalNotes || '',
    recommendation: mv.kypData?.recommendation || app?.kypData?.recommendation || '',
    letterRemarks: mv.kypData?.letterRemarks || app?.kypData?.letterRemarks || '',
    internalRemarks: mv.kypData?.internalRemarks || app?.kypData?.internalRemarks || '',
  validityPeriod: mv.kypData?.validityPeriod || '15 days',
  };

  return mergedKyp;
};

export const downloadKYPPDFFromFirestore = async (applicationId, options = { template: 'professional' }) => {
  try {
    if (!applicationId) throw new Error('applicationId is required');
    const result = await getApplication(applicationId);
    if (!result.success) throw new Error(result.error || 'Application not found');
    const application = result.application;
    const kypData = composeRealtimeKYPData(application);
    // Attempt to fetch lead generator details for logo embedding
    let leadGeneratorData;
    try {
      const lgId = application?.leadGenerator?.leadGeneratorId;
      if (lgId) {
        const lgRes = await getLeadGeneratorById(lgId);
        if (lgRes?.success) leadGeneratorData = lgRes.leadGenerator;
      }
    } catch (e) {
      console.warn('Lead generator fetch failed for PDF:', e?.message || e);
    }
    const fileName = `KYP_${options?.template === 'professional' ? 'Official' : 'Comprehensive'}_${applicationId}_${Date.now()}.pdf`;
    return await downloadKYPPDF(application, kypData, fileName, { ...options, leadGeneratorData });
  } catch (error) {
    console.error('Realtime KYP PDF error:', error);
    return { success: false, error: error.message };
  }
};

export const previewKYPPDFFromFirestore = async (applicationId, options = { template: 'professional' }) => {
  try {
    if (!applicationId) throw new Error('applicationId is required');
    const result = await getApplication(applicationId);
    if (!result.success) throw new Error(result.error || 'Application not found');
    const application = result.application;
    const kypData = composeRealtimeKYPData(application);
    // Attempt to fetch lead generator details for logo embedding
    let leadGeneratorData;
    try {
      const lgId = application?.leadGenerator?.leadGeneratorId;
      if (lgId) {
        const lgRes = await getLeadGeneratorById(lgId);
        if (lgRes?.success) leadGeneratorData = lgRes.leadGenerator;
      }
    } catch (e) {
      console.warn('Lead generator fetch failed for PDF preview:', e?.message || e);
    }
    return await previewKYPPDF(application, kypData, { ...options, leadGeneratorData });
  } catch (error) {
    console.error('Realtime KYP preview error:', error);
    return { success: false, error: error.message };
  }
};

// FAL (Final Approval Letter) - professional template matching KYP structure
export const generateFALPDF = async (application, { remarks = '', template = 'professional' } = {}) => {
  try {
    // Attempt to fetch lead generator details for logo embedding (parity with KYP)
    let leadGeneratorData;
    try {
      const lgId = application?.leadGenerator?.leadGeneratorId;
      if (lgId) {
        const lgRes = await getLeadGeneratorById(lgId);
        if (lgRes?.success) leadGeneratorData = lgRes.leadGenerator;
      }
    } catch (e) {
      console.warn('Lead generator fetch failed for FAL PDF:', e?.message || e);
    }
    const document = template === 'professional'
      ? <ProfessionalFALDocument application={application} remarks={remarks} leadGeneratorData={leadGeneratorData} />
      : <ComprehensiveFALDocument application={application} remarks={remarks} leadGeneratorData={leadGeneratorData} />;
    const pdfBlob = await pdf(document).toBlob();
    return { success: true, blob: pdfBlob, fileName: `FAL_${template === 'professional' ? 'Official' : 'Comprehensive'}_${application?.applicationId || Date.now()}_${Date.now()}.pdf` };
  } catch (error) {
    console.error('Error generating FAL PDF:', error);
    return { success: false, error: error.message };
  }
};

export const previewFALPDFFromFirestore = async (applicationId, { remarks = '' } = {}) => {
  try {
    if (!applicationId) throw new Error('applicationId is required');
    const result = await getApplication(applicationId);
    if (!result.success) throw new Error(result.error || 'Application not found');
    const application = result.application;
    const gen = await generateFALPDF(application, { remarks });
    if (!gen.success) throw new Error(gen.error || 'Failed');
    const url = URL.createObjectURL(gen.blob);
    window.open(url, '_blank');
    return { success: true };
  } catch (error) {
    console.error('Realtime FAL preview error:', error);
    return { success: false, error: error.message };
  }
};

export const downloadFALPDF = async (application, { remarks = '', template = 'comprehensive' } = {}) => {
  try {
    const result = await generateFALPDF(application, { remarks, template });
    if (!result.success) throw new Error(result.error || 'Failed to generate FAL');
    const url = URL.createObjectURL(result.blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = result.fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    return { success: true };
  } catch (error) {
    console.error('Error downloading FAL PDF:', error);
    return { success: false, error: error.message };
  }
};
